import { useMemo } from 'react';
import type { GlCostLine } from '@/lib/apiClient';
import { glLinesFor, actualByGroup } from '@/lib/glByGroup';

/**
 * One cost code's ACTUAL cost by cost group (#664), as an extra row under the code in the Job Cost
 * Detail. Built from the same GL entries the drawer behind the Actual figure lists, through the same
 * filter (`lib/glByGroup`), so the groups here add up to the figure on the row above.
 *
 * ⚠️ ACTUAL ONLY, ON PURPOSE. The table files a cost CODE under one cost type, the one the estimate
 * budgeted it as, because comparing actual to budget at the (code × type) grain manufactured phantom
 * overruns (see GlDetailDrawer). So this view shows what was SPENT by group and never sets a budget,
 * a committed figure or a cost to complete against a group. It also says so, so nobody reads the
 * absence of a budget split as a bug.
 */

/**
 * The rules for this row, declared HERE beside the classes that use them. They are a string, not a
 * <style> element, because the row sits inside a <tbody> where a <style> is invalid markup; the
 * Financials tab puts them in its own style block, outside the table.
 */
export const actualByTypeCss = `
  .fin-bytype td { background: var(--ew-n-50); padding: 8px 14px 10px 28px; border-top: 0; text-align: left; }
  .fin-bytype-list { display: flex; flex-wrap: wrap; gap: 6px 22px; font-variant-numeric: tabular-nums; }
  .fin-bytype-item { display: inline-flex; align-items: baseline; gap: 6px; font-size: 12.5px; color: var(--ew-dark); }
  .fin-bytype-item span { font-size: 11.5px; color: var(--ew-n-500); }
  .fin-bytype-note { margin-top: 6px; font-size: 11.5px; color: var(--ew-n-500); line-height: 1.45; max-width: 90ch; }
  .fin-bytype-warn { margin-top: 6px; font-size: 11.5px; color: #8A4E12; }
`;

const money = (v: number) => (v < 0 ? '-' : '') + '$' + Math.abs(Math.round(v)).toLocaleString('en-US');

export default function ActualByType({
  id, entries, failed, codes, through, closed, expected, colSpan,
}: {
  /** The id the row's expander points at (`aria-controls`). */
  id: string;
  /** The job's GL entries (the SAME read the drawer uses, so one fetch per job); undefined while it loads. */
  entries: readonly GlCostLine[] | undefined;
  /** The read of the entries failed. */
  failed: boolean;
  /** The month shown is CLOSED: the row's Actual is what Close stored, not today's GL. */
  closed: boolean;
  /** The cost codes that formed the row (`''` = the no-code bucket). */
  codes: string[];
  /** A past month's last day ('YYYY-MM-DD'), else null: the cut the row's own Actual was made with. */
  through: string | null;
  /** The row's Actual, to say when the entries and the figure came from different reads. */
  expected: number;
  colSpan: number;
}) {
  const view = useMemo(() => {
    if (!entries) return null;
    const lines = glLinesFor(entries, codes, through);
    return { count: lines.length, ...actualByGroup(lines) };
  }, [entries, codes, through]);

  const body = (() => {
    if (failed) return <div className="fin-bytype-warn">Could not load the entries behind this figure. Nothing has been changed.</div>;
    if (!view) return <div className="fin-bytype-note">Loading…</div>;
    if (!view.available) {
      return <div className="fin-bytype-warn">The cost group of each entry is not available yet: the server needs its latest update.</div>;
    }
    if (view.count === 0) return <div className="fin-bytype-note">No GL entries posted against this code yet.</div>;
    // A share is only meaningful when nothing is a credit: with a negative group the shares of a total
    // would run past 100% or below 0, so they are left off rather than shown as nonsense.
    const showShare = view.total > 0 && view.groups.every((g) => g.amount >= 0);
    // The entries and the row's Actual are the same read of the GL, so this should never fire.
    const drift = Math.abs(view.total - expected);
    return (
      <>
        <div className="fin-bytype-list" role="list" aria-label="Actual cost by cost type">
          {view.groups.length === 0
            ? <div className="fin-bytype-item" role="listitem">Entries net to {money(0)}</div>
            : view.groups.map((g) => (
              <div className="fin-bytype-item" role="listitem" key={g.group}>
                <b>{g.group}</b> {money(g.amount)}
                {showShare && <span>{Math.round((g.amount / view.total) * 100)}%</span>}
              </div>
            ))}
        </div>
        {/* ⚠️ A CLOSED month's row is the figure Close stored, but this split is read from TODAY's GL entries, which
            Intacct can still change: re-type an entry, or swap it for an equal one in another group, without moving
            the total. So equal totals prove nothing about the groups, and the split is labeled as not final whether
            or not the totals agree (Codex, #667). Storing the split at close would make it final; it does not. */}
        {closed && (
          <div className="fin-bytype-warn">
            This month is closed: the row is the figure Close stored. This split is read from today&rsquo;s GL entries, so it
            is not final and can differ from the month as it closed.
          </div>
        )}
        {drift > 1 && (
          <div className="fin-bytype-warn">
            {closed
              // Refreshing cannot make today's entries agree with the stored figure.
              ? <>These entries total {money(view.total)} but this closed month&rsquo;s row shows {money(expected)}.</>
              : <>These entries total {money(view.total)} but the row shows {money(expected)}. The GL feed has moved since the tab
                loaded; refresh the page for figures that agree.</>}
          </div>
        )}
      </>
    );
  })();

  return (
    <tr className="fin-bytype" id={id}>
      <td colSpan={colSpan}>
        {body}
        <div className="fin-bytype-note">
          Actual cost by the cost type on each GL entry. The budget is not split this way.
          {view?.available && view.groups.some((g) => g.group === 'Other') && ' Cost types the Tracker does not map show under Other.'}
        </div>
      </td>
    </tr>
  );
}
